'use strict';
const axios = require('axios');

const PROTOCOL = 'https://';
const DOMAIN_NAME = 'api.cloud.nuskin.com/feature-flags/v1/proxy';

const DEV_FEATURE_FLAG_ENDPOINT = `${PROTOCOL}dev${DOMAIN_NAME}`;
const TEST_FEATURE_FLAG_ENDPOINT = `${PROTOCOL}test${DOMAIN_NAME}`;
const STAGE_FEATURE_FLAG_ENDPOINT = `${PROTOCOL}stage${DOMAIN_NAME}`;
const PROD_FEATURE_FLAG_ENDPOINT = `${PROTOCOL}${DOMAIN_NAME}`;

const OVERRIDE_FLAG_ENABLE_PARAM = '$enableFeatureFlags';
const OVERRIDE_FLAG_DISABLE_PARAM = '$disableFeatureFlags';
const SESSION_STORAGE_USER = '$featureFlagUserId';

const endpoints = {
    dev: DEV_FEATURE_FLAG_ENDPOINT,
    test: TEST_FEATURE_FLAG_ENDPOINT,
    stage: STAGE_FEATURE_FLAG_ENDPOINT,
    prod: PROD_FEATURE_FLAG_ENDPOINT
};

// If these are updated, be sure to update the retrieveTogglesBasedOnEnvironment documentation and the README
// these mappings will be executed in the order in which they are listed
// changing the order could change how the system functions
const environmentMappings = [
    // dev
    { expression: /^dev$/, mapping: endpoints.dev },
    { expression: /^localhost(:[0-9]*)?$/, mapping: endpoints.dev },
    { expression: /.*pubaws.*/, mapping: endpoints.dev },
    { expression: /.*authaws.*/, mapping: endpoints.dev },
    { expression: /.*aem\.dev\.nuskin\.io.*/, mapping: endpoints.dev },
    { expression: /^tools\.dev\.nuskin\.io$/, mapping: endpoints.dev },
    { expression: /^dev\.nuskin\.com$/, mapping: endpoints.dev },
    { expression: /^devapi\.cloud\.nuskin\.com$/, mapping: endpoints.dev },
    { expression: /^development$/, mapping: endpoints.dev },

    // test
    { expression: /^test$/, mapping: endpoints.test },
    { expression: /^test\.nuskin\.com$/, mapping: endpoints.test },
    { expression: /^apps\.dev\.nuskin\.io$/, mapping: endpoints.test },
    { expression: /^tools\.tst\.nuskin\.io$/, mapping: endpoints.test },
    { expression: /.*\.test\.mynuskin\.com$/, mapping: endpoints.test },
    { expression: /^testapi\.cloud\.nuskin\.com$/, mapping: endpoints.test},
    { expression: /^www.tst.nuskin.io$/, mapping: endpoints.test},

    // stage
    { expression: /^stage$/, mapping: endpoints.stage},
    { expression: /^tools\.stage\.nuskin\.io$/, mapping: endpoints.stage },
    { expression: /^stage\.nuskin\.com$/, mapping: endpoints.stage},
    { expression: /^stageapi\.cloud\.nuskin\.com$/, mapping: endpoints.stage},

    // prod
    { expression: /^prod$/, mapping: endpoints.prod },
    { expression: /^www\.nuskin\.com$/, mapping: endpoints.prod},
    { expression: /^tools\.nuskin\.io$/, mapping: endpoints.prod },
    { expression: /^api\.cloud\.nuskin\.com$/, mapping: endpoints.prod},
    { expression: /[^\\.]*\.mynuskin\.com$/, mapping: endpoints.prod },
    { expression: /^production$/, mapping: endpoints.prod}
];

const THRESHOLD = 5000; // 5 seconds backaway

const getSandboxedFlagInstance = () => {

    // The local variable toggles is used to store the enabled flag names
    let toggles = new Set();
    let timestamp = 0;
    let currentPromise;

    /**
     * retrieveToggles is a function to retrieve the feature toggles from a given URL.
     * (e.g., https://devapi.cloud.nuskin.com/feature-flags/v1/proxy)
     *
     * @param {string} url the URL for the retrieval of the feature flags
     * @param {string} userId an optional userId for the targeted user
     * @returns {Set<string>} a set of the currently-enabled feature flags
     */
    async function retrieveToggles(url, userId) {
        // Make sure there is no query parameter for the URL
        if (url && (url.indexOf('?') === -1)) {
            // Make sure that the userId does NOT contain
            if (userId) {
                if (userId.indexOf('&') >= 0) {
                    console.error('Unexpected character in userId:', userId);
                } else {
                    url += `?userId=${userId}`;
                }
            } else {
                const ssUser = getSessionStorageUser();
                if(ssUser){
                    url += `?userId=${ssUser}`;
                }
            }

            const now = new Date();
            // If we've fetched flags recently, don't do it again
            if ((timestamp <= 0) || (now - timestamp) >= THRESHOLD) {
                currentPromise = doRetrieveToggles(url);
            }
            // If we're in the process of fetching flags, wait for that
            // other call to finish
            if (currentPromise) {
                await currentPromise;
            }
        } else {
            console.error('Invalid url to retrieve feature flags, url=', url);
        }
        return getEnabledFlags();
    }

    async function doRetrieveToggles(url) {
        try {
            timestamp = Date.now(); // update timestamp so that it will not call multiple time
            const response = await (axios.get || axios.default.get)(url);
            if (Array.isArray(response.data.toggles)) {
                setTogglesFromResponse(response.data.toggles);
            } else {
                console.error('Invalid toggles (should be an array)');
            }
        } catch (err) {
            console.error('Cannot retrieve toggles:', err);
        } finally {
            currentPromise = null;
        }
    }

    /**
     * Gets the proxy url based on a provided key (hostname).
     * @param {string} key The key to use to lookup the url.
     *      This can be a hostname or environment name (dev, development, test, prod, production, etc.)
     */
    function getUrlBasedOnKey(key) {
        let url;
        try {
            let lowerCaseKey = key ? key.toLowerCase() : null;
            for(const { expression, mapping } of Object.values(environmentMappings)) {
                if (expression.test(lowerCaseKey)) {
                    url = mapping;
                    break;
                }
            }
        }
        // eslint-disable-next-line no-empty
        catch (error) {
            
        }

        if (!url) {
            console.log('No valid url to match key, defaulting to prod key. key=', key);
            url = endpoints.prod;
        }

        return url;
    }


    /**
     * This function will take the options provided, and return feature toggles based on the environment.
     * If no options are provided, it will default to using the "window.location.hostname", which should not be used in the
     * Node environment.
     * 
     * If no suitable matches can be found (hostname, environment or other cases), the default is to look for toggles in
     * the production environment.
     * 
     * @param {Object} options Options to use in retrieving the toggles based on the environment.
     * @param {string} options.hostname The hostname of the consuming application. Do not use in Node.
     * @param {string} options.environment The environment from the consuming application. Use this in Node.
     *      This could be something like: "dev", "develop", "test", "stage", "prod", "production".
     *      Further, this could be in an environment variable, and passed in like this:
     *      retrieveTogglesBasedOnEnvironment(process.env.ENVIRONMENT)
     * @param {string} options.userId Retreives toggles for this user id.
     */
    async function retrieveTogglesBasedOnEnvironment(options = {
        hostname: '',
        environment: '',
        userId: null
    }) {
        let url = '';
        if (options.hostname && typeof options.hostname === 'string') {
            url = getUrlBasedOnKey(options.hostname);
        } else if (options.environment && typeof options.environment === 'string') {
            url = getUrlBasedOnKey(options.environment);
        } else {
            const hostname = (typeof window !== "undefined") && window.location && window.location.hostname
                ? window.location.hostname
                : endpoints.prod;
            url = getUrlBasedOnKey(hostname);
        }
        const toggles = await retrieveToggles(url, options.userId);
        return toggles;
    }

    /**
     * retrieveProductionToggles is a function to just retrieve the feature toggles from Production.
     *
     * @param {string} userId an optional userId for the targeted user
     * @returns {Set<string>} a set of the currently-enabled feature flags
     */
    async function retrieveProductionToggles(userId) {
        return await retrieveToggles(endpoints.prod, userId);
    }

    /**
     * isEnabled is a function to check if a given toggleName is enabled.
     *
     * @param {string} toggleName the name of the feature flag to check
     * @return {boolean} to indicate if the feature flag is enabled or not
     */
    function isEnabled(toggleName) {
        return toggles.has(toggleName);
    }

    /**
     * Get the set of enabled flag names
     * @return {Set<string>} feature flag names
     */
    function getEnabledFlags() {
        // Defensive copy
        return new Set([...toggles]);
    }

    //-------------------------------------------------------
    //-------------- Private utility functions --------------
    //-------------------------------------------------------

    /**
     * From the server response array, set the toggles var, including any changes
     * from local user overrides.
     * @param {[]]} togglesArray array from server response
     */
    function setTogglesFromResponse(togglesArray) {
        const localOverrides = getFlagOverrides();
        const fromServer = togglesArray.filter(t => t.enabled).map(t => t.name);

        // Turn into a nice, fast set
        const result = new Set(fromServer);

        // If we have an override...
        for (const [key, value] of localOverrides) {
            if (value) {
                // ...and it's true, make sure it's in the set
                result.add(key);
            } else {
                // ...otherwise, make sure it's NOT in the set
                result.delete(key);
            }
        }
        toggles = result;
    }

    /**
     * Get a map of all feature flag overrides from query string and session storage.
     * 
     * @return {Map<string, boolean>} flag names to override values
     */
    function getFlagOverrides() {
        const disabledPairs = getOverrideList(OVERRIDE_FLAG_DISABLE_PARAM)
            .map(it => [it, false]);
        const enabledPairs = getOverrideList(OVERRIDE_FLAG_ENABLE_PARAM)
            .map(it => [it, true]);

        return new Map([...disabledPairs, ...enabledPairs]);
    }

    /**
     * Get a list of override names from query string and session storage
     * for a given parameter name.
     * 
     * @param {string} paramName param name
     * @return {string[]} feature flag names
     */
    function getOverrideList(paramName) {
        const query = getQueryOverrides(paramName);
        const session = getSessionOverrides(paramName);

        return [...query, ...session];
    }

    /**
     * Get a list of feature names from the given query param
     * 
     * @param {string} paramName param name
     * @return {string[]} feature flag names
     */
    function getQueryOverrides(paramName) {
        // If we're in a browser-ish environment, grab the querystring
        if ((typeof window !== "undefined") && window.location && window.location.search) {
            const query = new URLSearchParams(window.location.search);

            return query.getAll(paramName)
                .map(each => each.split(','))
                // flatMap for envs that don't yet support flatMap
                //  gotta do the flatMap because there can be multiple query params
                //  with the same name
                .reduce((a, each) => a.concat(...each), []);
        } else {
            return [];
        }
    }

    /**
     * Get a list of feature names from the given session storage value
     * 
     * @param {string} paramName param name
     * @return {string[]} feature flag names
     */
    function getSessionOverrides(paramName) {
        /* istanbul ignore next */
        if ((typeof sessionStorage === "undefined") || !sessionStorage) {
            // If we're not in a browser, there's no session storage, so bail
            return [];
        }
        const found = sessionStorage.getItem(paramName);
        if (found) {
            return found.split(',');
        } else {
            return [];
        }
    }

    /**
     * getSessionStorageUser
     * 
     * @returns {Set<string>} a set of the currently-enabled feature flags
     */
    function getSessionStorageUser() {
        let ffid = '';
        /* istanbul ignore next */
        if (typeof window !== "undefined"){
            /* istanbul ignore next */
            if (window && window.sessionStorage && window.sessionStorage.getItem(SESSION_STORAGE_USER)) {
                ffid = window.sessionStorage.getItem(SESSION_STORAGE_USER);
            }
        }

        return ffid;
    }

    return {
        getEnabledFlags,
        getUrlBasedOnKey,
        isEnabled,
        retrieveProductionToggles,
        retrieveToggles,
        retrieveTogglesBasedOnEnvironment
    };
};

const {
    getEnabledFlags,
    getUrlBasedOnKey,
    isEnabled,
    retrieveProductionToggles,
    retrieveToggles,
    retrieveTogglesBasedOnEnvironment
} = getSandboxedFlagInstance();


module.exports = {
    endpoints,
    getEnabledFlags,
    getUrlBasedOnKey,
    isEnabled,
    retrieveProductionToggles,
    retrieveToggles,
    retrieveTogglesBasedOnEnvironment,
    getSandboxedFlagInstance
};
